import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import MenuItem from '@mui/material/MenuItem'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Add from '@mui/icons-material/Add'
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported'
import SearchIcon from '@mui/icons-material/Search'
import API from '../../component/Axios'
import EditIcon from '../../component/EditIcon'
import ListActionGroup from '../../component/ListActionGroup'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import ListTableToolbar, { LIST_TABLE_TOOLBAR_BUTTON_SX } from '../../component/ListTableToolbar'
import Pagination from '../../component/Pagination'
import ResetFilterButton from '../../component/ResetFilterButton'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import StatusChip from '../../component/StatusChip'
import Title from '../../component/Title'
import { LIST_FILTER_SEARCH_BUTTON_SX, LIST_FILTER_SEARCH_FIELD_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import { renderDateRange } from '../../utils/dateDisplay'
import { createInitialListState, createListLinkTarget, buildListSearch as buildListSearchQuery } from '../../utils/listPageState'

const DEFAULT_SEARCH_SELECT = {
    status: '',
}

const SEARCH_SELECT_PARAMS = {
    status: {
        param: 'status',
        defaultValue: DEFAULT_SEARCH_SELECT.status,
    },
}

const PACKAGE_TABLE_COLUMNS = [
    { width: 64 },
    { width: 68 },
    { width: 260 },
    { width: 120 },
    { width: 90 },
    { width: 110 },
    { width: 160 },
    { width: 90 },
    { width: 96 },
]

const PACKAGE_TABLE_SX = {
    minWidth: PACKAGE_TABLE_COLUMNS.reduce((sum, column) => sum + column.width, 0),
    tableLayout: 'fixed',
    '& th': {
        whiteSpace: 'nowrap',
    },
    '& td': {
        verticalAlign: 'middle',
    },
    '& tbody tr': {
        height: 58,
    },
}

const TITLE_TEXT_SX = {
    display: '-webkit-box',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    lineHeight: 1.45,
    wordBreak: 'break-word',
}

const NOWRAP_CELL_SX = {
    whiteSpace: 'nowrap',
}

const thumbnailPlaceholderSx = {
    width: 32,
    height: 32,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.default',
    color: 'text.disabled',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    verticalAlign: 'middle',
}

const getInitialListState = (location) => createInitialListState({
    location,
    defaultSearchSelect: DEFAULT_SEARCH_SELECT,
    selectParams: SEARCH_SELECT_PARAMS,
})

const buildListSearch = ({ curPage, searchQuery, searchSelect }) => buildListSearchQuery({
    curPage,
    searchQuery,
    searchSelect,
    selectParams: SEARCH_SELECT_PARAMS,
})

const getPackageListTotalCount = (count) => {
    if (Array.isArray(count)) {
        return count.length
    }

    return Number(count || 0)
}

const getThumbnailSrc = (product = {}) => {
    const thumbnail = product.thumbnail ? String(product.thumbnail) : String(product.mainImage || '')

    if (!thumbnail) {
        return ''
    }

    if (thumbnail.includes('animal')) {
        return `${process.env.REACT_APP_AWS_S3_URL}/${thumbnail}`
    }

    if (!product.adminId) {
        return ''
    }

    return `${process.env.REACT_APP_SERVER_URL}/product/${product.adminId}/${thumbnail}`
}

const renderThumbnail = (product) => {
    const thumbnailSrc = getThumbnailSrc(product)

    if (!thumbnailSrc) {
        return (
            <Box sx={thumbnailPlaceholderSx} aria-label="패키지 상품 이미지 없음">
                <ImageNotSupportedIcon sx={{ fontSize: 18 }} />
            </Box>
        )
    }

    return <img className="listImg" src={thumbnailSrc} alt={product.title || '패키지 상품 이미지'} />
}

const renderTitle = (title) => (
    <Box component="span" title={title || '-'} sx={TITLE_TEXT_SX}>
        {title || '-'}
    </Box>
)

const renderStatusChip = (status) => (
    Number(status) === 1
        ? <StatusChip label="활성" color="success" />
        : <StatusChip label="비활성" variant="outlined" />
)

export default function AnimalPackages() {
    const location = useLocation()
    const history = useHistory()
    const initialListStateRef = useRef(getInitialListState(location))
    const initialListState = initialListStateRef.current
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [data, setData] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery)
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const [listErrorMessage, setListErrorMessage] = useState('')
    const offset = 10

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setActionLoading(true)
        setListErrorMessage('')

        try {
            const { data: response } = await API.post('/api/v1/admin/animalPackage/getList', {
                page: curPage,
                offset,
                searchText: searchQuery,
                searchSelect,
            })

            if ([1, 200].includes(Number(response.code))) {
                setTotalCount(getPackageListTotalCount(response.data?.count))
                setData(response.data?.rows || [])
                setListErrorMessage('')
            } else if (response.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = response.message || '패키지 상품 목록을 불러오지 못했습니다.'

                setTotalCount(0)
                setData([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '패키지 상품 목록을 불러오지 못했습니다.'

            setTotalCount(0)
            setData([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
            setActionLoading(false)
        }
    }, [curPage, history, searchQuery, searchSelect])

    useEffect(() => {
        getData()
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearch({ curPage, searchQuery, searchSelect })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, location.pathname, location.search, searchQuery, searchSelect])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const updateSearchSelect = (e) => {
        const { name, value } = e.target

        if (actionLoading) {
            return
        }

        setCurPage(1)
        setSearchQuery((searchText || '').trim())
        setSearchSelect((prevSearchSelect) => ({
            ...prevSearchSelect,
            [name]: value,
        }))
    }

    const searchButtonClick = () => {
        if (actionLoading) {
            return
        }

        const trimmedSearchText = (searchText || '').trim()

        if (curPage !== 1) {
            setCurPage(1)
        }

        setSearchQuery(trimmedSearchText)
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    const resetFilters = () => {
        if (actionLoading) {
            return
        }

        setCurPage(1)
        setSearchText('')
        setSearchQuery('')
        setSearchSelect({ ...DEFAULT_SEARCH_SELECT })
    }

    const hasActiveFilters = () => {
        if ((searchQuery || '').trim()) {
            return true
        }

        return Object.entries(DEFAULT_SEARCH_SELECT).some(([key, defaultValue]) => {
            const currentValue = searchSelect?.[key] ?? defaultValue

            return String(currentValue) !== String(defaultValue)
        })
    }

    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="패키지 상품 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button type="button" size="small" variant="outlined" onClick={() => getData(false)} disabled={actionLoading}>
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters()) {
            return (
                <ListEmptyState
                    title="조건에 맞는 패키지 상품이 없습니다."
                    description="검색어 또는 필터를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={actionLoading} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="조회된 패키지 상품이 없습니다."
                description="패키지 상품을 등록하면 이 목록에 표시됩니다."
            />
        )
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Grid item xs={12}>
                    <Spiner />
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose} />}
                </Grid>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Title>패키지 상품 리스트</Title>
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={actionLoading} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="상품명 또는 패키지 코드"
                                type="search"
                                name="searchText"
                                value={searchText}
                                disabled={actionLoading}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={actionLoading}><SearchIcon /></IconButton>
                            <Divider sx={{ height: 28, m: 0.5, marginRight: 2 }} orientation="vertical" />
                            <TextField
                                label="상태"
                                select
                                name="status"
                                value={searchSelect.status}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={actionLoading}
                                sx={{ minWidth: 150 }}
                            >
                                <MenuItem value="">전체</MenuItem>
                                <MenuItem value="1">활성</MenuItem>
                                <MenuItem value="0">비활성</MenuItem>
                            </TextField>
                        </ListFilterForm>
                    </ListFilterBar>

                    <ListTableToolbar
                        right={(
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<Add />}
                                size="small"
                                disabled={actionLoading}
                                sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                                onClick={() => history.push(createListLinkTarget({
                                    pathname: '/new-animal-package',
                                    search: location.search,
                                    curPage,
                                    searchText: searchQuery,
                                    searchSelect,
                                }))}
                            >
                                등록
                            </Button>
                        )}
                    />
                    <ListTableContainer>
                        <Table size="small" sx={PACKAGE_TABLE_SX}>
                            <colgroup>
                                {PACKAGE_TABLE_COLUMNS.map((column, index) => (
                                    <col key={`animal-package-column-${index}`} style={{ width: column.width }} />
                                ))}
                            </colgroup>
                            <caption>Total Count: {totalCount}</caption>
                            <TableHead>
                                <TableRow>
                                    <TableCell>No</TableCell>
                                    <TableCell>이미지</TableCell>
                                    <TableCell>상품명</TableCell>
                                    <TableCell>패키지 코드</TableCell>
                                    <TableCell align="center">구 수</TableCell>
                                    <TableCell align="center">상태</TableCell>
                                    <TableCell>접수일</TableCell>
                                    <TableCell>노출</TableCell>
                                    <TableCell align="right">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.length > 0 ? data.map((row) => {
                                    const product = row.product || {}
                                    const regions = row.animal_package_region || []

                                    return (
                                        <TableRow key={row.animalPackageId} sx={{ backgroundColor: Number(product.isShow) === 0 ? 'action.hover' : 'inherit' }}>
                                            <TableCell sx={NOWRAP_CELL_SX}>{row.rownum}</TableCell>
                                            <TableCell sx={NOWRAP_CELL_SX}>{renderThumbnail(product)}</TableCell>
                                            <TableCell>{renderTitle(product.title)}</TableCell>
                                            <TableCell sx={NOWRAP_CELL_SX}>{row.packageCode}</TableCell>
                                            <TableCell align="center" sx={NOWRAP_CELL_SX}>{regions.length}</TableCell>
                                            <TableCell align="center" sx={NOWRAP_CELL_SX}>{renderStatusChip(row.status)}</TableCell>
                                            <TableCell sx={NOWRAP_CELL_SX}>{renderDateRange(product.startTime, product.endTime)}</TableCell>
                                            <TableCell sx={NOWRAP_CELL_SX}>{Number(product.isShow) === 0 ? '미노출' : '노출'}</TableCell>
                                            <TableCell align="right" sx={NOWRAP_CELL_SX}>
                                                <ListActionGroup disabled={actionLoading}>
                                                    <EditIcon path="animal-package" id={row.productId} search={location.search} curPage={curPage} searchText={searchQuery} searchSelect={searchSelect} />
                                                </ListActionGroup>
                                            </TableCell>
                                        </TableRow>
                                    )
                                }) : (
                                    <TableRow>
                                        <TableCell colSpan={9}>
                                            {renderEmptyState()}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose} />}
                </Paper>
            )}
        </Grid>
    )
}
